import { type FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Permission,
  roleHasPermissions,
  canAssignRole,
  orgRoleSchema,
  type OrgRole,
  type Member,
} from '@lizrd/contracts';
import {
  changeMemberRole,
  createInvitation,
  getSubscriptionUsage,
  listDomains,
  listInvitations,
  listMembers,
  removeMember,
  resetMemberMfa,
  revokeInvitation,
  setMemberDomains,
} from '../lib/api';
import { roleLabel } from '../lib/roles';
import { useAuthStore } from '../store/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Modal } from '../components/ui/modal';
import { Select, type SelectOption } from '../components/ui/select';
import { FilterChip } from '../components/ui/filter-chip';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Pagination } from '../components/ui/pagination';
import { SkeletonRows } from '../components/ui/skeleton';
import { UsageMeter } from '../components/UsageMeter';
import { DomainAccessChecklist } from '../components/DomainAccessChecklist';

const ROLES = orgRoleSchema.options;
const PAGE_SIZE = 25;

// Administrators and Managers reach every domain implicitly (RBAC-005/006); only
// Engineers and Viewers gain access through explicit per-domain grants.
const isEnvScoped = (role: OrgRole) => role === 'ENGINEER' || role === 'VIEWER';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
const fmtLastActive = (iso: string | null) => (iso ? fmtDate(iso) : 'never');

type RoleFilter = OrgRole | 'all';

export function MembersPage() {
  const currentUser = useAuthStore((s) => s.user);
  const role = currentUser?.role;
  const canManage = role ? roleHasPermissions(role, [Permission.UsersManage]) : false;
  const canManageDomains = role ? roleHasPermissions(role, [Permission.DomainsManage]) : false;
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('VIEWER');
  const [inviteDomainIds, setInviteDomainIds] = useState<string[]>([]);
  const [memberQ, setMemberQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [memberPage, setMemberPage] = useState(0);
  const [inviteQ, setInviteQ] = useState('');
  const [invitePage, setInvitePage] = useState(0);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [resetMfaTarget, setResetMfaTarget] = useState<Member | null>(null);
  // The member whose domain access is being edited, plus the in-progress selection.
  const [manageTarget, setManageTarget] = useState<Member | null>(null);
  const [manageDraft, setManageDraft] = useState<string[]>([]);

  const members = useQuery({ queryKey: ['members'], queryFn: listMembers });
  const invitations = useQuery({ queryKey: ['invitations'], queryFn: listInvitations });
  const usage = useQuery({ queryKey: ['subscription'], queryFn: getSubscriptionUsage });
  const domains = useQuery({ queryKey: ['domains'], queryFn: listDomains });
  const domainList = domains.data ?? [];
  const domainName = useMemo(() => new Map(domainList.map((d) => [d.id, d.name])), [domainList]);

  const onError = (e: unknown) => setError(e instanceof Error ? e.message : 'Something went wrong');
  const seatChanged = () => {
    qc.invalidateQueries({ queryKey: ['invitations'] });
    qc.invalidateQueries({ queryKey: ['members'] });
    qc.invalidateQueries({ queryKey: ['subscription'] });
  };

  const invite = useMutation({
    mutationFn: () =>
      createInvitation({
        email: inviteEmail.trim(),
        role: inviteRole,
        // Only env-scoped roles carry domain grants; admins/managers reach all.
        domainIds: isEnvScoped(inviteRole) ? inviteDomainIds : [],
      }),
    onSuccess: () => {
      setInviteEmail('');
      setInviteDomainIds([]);
      setError(null);
      seatChanged();
    },
    onError,
  });
  const saveDomains = useMutation({
    mutationFn: ({ userId, domainIds }: { userId: string; domainIds: string[] }) =>
      setMemberDomains(userId, domainIds),
    onSuccess: () => {
      setManageTarget(null);
      qc.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (e) => {
      setManageTarget(null);
      onError(e);
    },
  });

  function openManageDomains(m: Member) {
    setManageDraft(m.domainIds);
    setManageTarget(m);
  }
  const revoke = useMutation({ mutationFn: revokeInvitation, onSuccess: seatChanged, onError });
  const setRole = useMutation({
    mutationFn: ({ userId, next }: { userId: string; next: OrgRole }) =>
      changeMemberRole(userId, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
    onError,
  });
  const remove = useMutation({
    mutationFn: removeMember,
    onSuccess: () => {
      setRemoveTarget(null);
      seatChanged();
    },
    onError: (e) => {
      setRemoveTarget(null);
      onError(e);
    },
  });
  const resetMfa = useMutation({
    mutationFn: resetMemberMfa,
    onSuccess: () => {
      setResetMfaTarget(null);
      qc.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (e) => {
      setResetMfaTarget(null);
      onError(e);
    },
  });

  function onInvite(e: FormEvent) {
    e.preventDefault();
    if (inviteEmail.trim()) invite.mutate();
  }

  const assignableRoles = ROLES.filter((r) => (role ? canAssignRole(role, r) : false));
  const roleOptions: SelectOption<OrgRole>[] = assignableRoles.map((r) => ({
    value: r,
    label: roleLabel(r),
  }));

  // Client-side search + role filter + pagination (the roster endpoint returns
  // the whole org). Role-chip counts reflect the search-narrowed set.
  const allMembers = members.data ?? [];
  const searchedMembers = useMemo(() => {
    const needle = memberQ.trim().toLowerCase();
    if (!needle) return allMembers;
    return allMembers.filter(
      (m) =>
        (m.name ?? '').toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle),
    );
  }, [allMembers, memberQ]);
  const roleCounts = useMemo(() => {
    const counts = new Map<OrgRole, number>();
    for (const m of searchedMembers) counts.set(m.role, (counts.get(m.role) ?? 0) + 1);
    return counts;
  }, [searchedMembers]);
  const filteredMembers =
    roleFilter === 'all' ? searchedMembers : searchedMembers.filter((m) => m.role === roleFilter);

  const memberPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));
  const memberClamped = Math.min(memberPage, memberPages - 1);
  const memberStart = memberClamped * PAGE_SIZE;
  const visibleMembers = filteredMembers.slice(memberStart, memberStart + PAGE_SIZE);

  const allInvites = invitations.data ?? [];
  const filteredInvites = useMemo(() => {
    const needle = inviteQ.trim().toLowerCase();
    if (!needle) return allInvites;
    return allInvites.filter((i) => i.email.toLowerCase().includes(needle));
  }, [allInvites, inviteQ]);
  const invitePages = Math.max(1, Math.ceil(filteredInvites.length / PAGE_SIZE));
  const inviteClamped = Math.min(invitePage, invitePages - 1);
  const inviteStart = inviteClamped * PAGE_SIZE;
  const visibleInvites = filteredInvites.slice(inviteStart, inviteStart + PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Members</h1>
          <p className="text-sm text-muted">People in your organization and pending invitations.</p>
        </div>
        {usage.data && (
          <UsageMeter label="users" used={usage.data.users.used} max={usage.data.users.max} />
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {canManage && (
        <Card className="p-5">
          <h2 className="mb-3 font-display text-base font-bold text-bone">Invite Your Team</h2>
          <form onSubmit={onInvite} className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Input
                type="email"
                placeholder="teammate@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="min-w-56 flex-1"
              />
              <Select
                value={inviteRole}
                onChange={(r) => {
                  setInviteRole(r);
                  if (!isEnvScoped(r)) setInviteDomainIds([]);
                }}
                options={roleOptions}
                ariaLabel="Invite role"
                className="w-40"
              />
              <Button type="submit" disabled={invite.isPending || !inviteEmail.trim()}>
                {invite.isPending ? 'Inviting…' : 'Invite'}
              </Button>
            </div>
            {isEnvScoped(inviteRole) && canManageDomains && domainList.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Domain access
                  <span className="ml-2 font-normal normal-case text-muted">
                    {inviteDomainIds.length} of {domainList.length} selected — granted when they
                    accept
                  </span>
                </p>
                <DomainAccessChecklist
                  domains={domainList}
                  value={inviteDomainIds}
                  onChange={setInviteDomainIds}
                />
              </div>
            )}
            {!isEnvScoped(inviteRole) && (
              <p className="text-xs text-muted">
                {roleLabel(inviteRole)}s reach every domain automatically.
              </p>
            )}
          </form>
        </Card>
      )}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Members</p>
          <Input
            placeholder="Search people by name or email…"
            value={memberQ}
            onChange={(e) => {
              setMemberQ(e.target.value);
              setMemberPage(0);
            }}
            className="w-full max-w-xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
            Role
          </span>
          <FilterChip
            active={roleFilter === 'all'}
            onClick={() => {
              setRoleFilter('all');
              setMemberPage(0);
            }}
          >
            All {searchedMembers.length}
          </FilterChip>
          {ROLES.filter((r) => (roleCounts.get(r) ?? 0) > 0).map((r) => (
            <FilterChip
              key={r}
              active={roleFilter === r}
              onClick={() => {
                setRoleFilter(r);
                setMemberPage(0);
              }}
            >
              {roleLabel(r)} {roleCounts.get(r)}
            </FilterChip>
          ))}
        </div>

        {members.isLoading ? (
          <SkeletonRows rows={6} rightBlocks={2} />
        ) : (
          <Card className="divide-y divide-hairline">
            {filteredMembers.length === 0 && (
              <p className="p-4 text-sm text-muted">
                {memberQ || roleFilter !== 'all'
                  ? 'No people match your filters.'
                  : 'No members yet.'}
              </p>
            )}
            {visibleMembers.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="truncate font-medium">{m.name ?? m.email}</p>
                  <p className="truncate text-xs text-muted">{m.email}</p>
                  <p className="mt-0.5 truncate text-[0.7rem] text-muted">
                    joined {fmtDate(m.createdAt)} · last active {fmtLastActive(m.lastLoginAt)}
                  </p>
                  <MemberDomainAccess
                    member={m}
                    domainName={domainName}
                    canManage={canManageDomains}
                    onManage={() => openManageDomains(m)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  {!m.emailVerified && <Badge variant="warning">unverified</Badge>}
                  {m.mfaEnabled && (
                    <Badge variant="success" dot>
                      2FA
                    </Badge>
                  )}
                  {canManage && m.id !== currentUser?.id && role && canAssignRole(role, m.role) ? (
                    <>
                      <Select
                        value={m.role}
                        onChange={(next) => setRole.mutate({ userId: m.id, next })}
                        options={roleOptions}
                        ariaLabel={`Role for ${m.name ?? m.email}`}
                        size="sm"
                        className="w-36"
                      />
                      {m.mfaEnabled && (
                        <Button size="sm" variant="ghost" onClick={() => setResetMfaTarget(m)}>
                          Reset 2FA
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setRemoveTarget(m)}>
                        Remove
                      </Button>
                    </>
                  ) : (
                    <Badge variant="neutral">{roleLabel(m.role)}</Badge>
                  )}
                </div>
              </div>
            ))}
          </Card>
        )}

        <Pagination
          page={memberClamped}
          pageCount={memberPages}
          total={filteredMembers.length}
          start={memberStart}
          shown={visibleMembers.length}
          onPrev={() => setMemberPage(memberClamped - 1)}
          onNext={() => setMemberPage(memberClamped + 1)}
        />
      </div>

      {(invitations.isLoading || allInvites.length > 0) && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Invitations</p>
            {allInvites.length > 0 && (
              <Input
                placeholder="Search invitations by email…"
                value={inviteQ}
                onChange={(e) => {
                  setInviteQ(e.target.value);
                  setInvitePage(0);
                }}
                className="w-full max-w-xs"
              />
            )}
          </div>
          {invitations.isLoading ? (
            <SkeletonRows rows={3} />
          ) : (
            <Card className="divide-y divide-hairline">
              {filteredInvites.length === 0 && (
                <p className="p-4 text-sm text-muted">No invitations match your search.</p>
              )}
              {visibleInvites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{inv.email}</p>
                    <p className="text-xs text-muted">{roleLabel(inv.role)}</p>
                    <p className="mt-0.5 text-[0.7rem] text-muted">
                      invited {fmtDate(inv.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={inv.status === 'PENDING' ? 'info' : 'neutral'}>
                      {inv.status.toLowerCase()}
                    </Badge>
                    {canManage && inv.status === 'PENDING' && (
                      <Button size="sm" variant="ghost" onClick={() => revoke.mutate(inv.id)}>
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          )}

          <Pagination
            page={inviteClamped}
            pageCount={invitePages}
            total={filteredInvites.length}
            start={inviteStart}
            shown={visibleInvites.length}
            onPrev={() => setInvitePage(inviteClamped - 1)}
            onNext={() => setInvitePage(inviteClamped + 1)}
          />
        </div>
      )}

      <ConfirmDialog
        open={!!removeTarget}
        tone="danger"
        title="Remove this member?"
        description={
          removeTarget ? (
            <>
              <strong className="text-bone">{removeTarget.name ?? removeTarget.email}</strong> (
              {removeTarget.email}) will lose access to this organization. Last active{' '}
              {fmtLastActive(removeTarget.lastLoginAt)}.
            </>
          ) : null
        }
        confirmLabel="Remove member"
        pending={remove.isPending}
        onConfirm={() => removeTarget && remove.mutate(removeTarget.id)}
        onCancel={() => setRemoveTarget(null)}
      />

      <ConfirmDialog
        open={!!resetMfaTarget}
        tone="danger"
        title="Reset this member's two-factor?"
        description={
          resetMfaTarget ? (
            <>
              This removes all two-factor methods (authenticator apps, passkeys, recovery codes) for{' '}
              <strong className="text-bone">{resetMfaTarget.name ?? resetMfaTarget.email}</strong>.
              Use this only for a genuine lockout — they&rsquo;ll sign in with their password and,
              if your org requires it, be prompted to set up two-factor again.
            </>
          ) : null
        }
        confirmLabel="Reset 2FA"
        pending={resetMfa.isPending}
        onConfirm={() => resetMfaTarget && resetMfa.mutate(resetMfaTarget.id)}
        onCancel={() => setResetMfaTarget(null)}
      />

      <Modal
        open={!!manageTarget}
        onClose={() => setManageTarget(null)}
        title={
          manageTarget ? (
            <div className="min-w-0">
              <h2 className="truncate font-display text-base font-bold text-bone">
                Domain access — {manageTarget.name ?? manageTarget.email}
              </h2>
              <p className="text-xs text-muted">
                Choose which domains this {roleLabel(manageTarget.role).toLowerCase()} can access.
              </p>
            </div>
          ) : null
        }
        footer={
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted">
              {manageDraft.length} of {domainList.length} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setManageTarget(null)}>
                Cancel
              </Button>
              <Button
                disabled={saveDomains.isPending}
                onClick={() =>
                  manageTarget &&
                  saveDomains.mutate({ userId: manageTarget.id, domainIds: manageDraft })
                }
              >
                {saveDomains.isPending ? 'Saving…' : 'Save access'}
              </Button>
            </div>
          </div>
        }
      >
        <DomainAccessChecklist
          domains={domainList}
          value={manageDraft}
          onChange={setManageDraft}
          emptyLabel="Create a domain first, then you can grant access here."
        />
      </Modal>
    </div>
  );
}

/** The domain-access line under each member row: chips for env-scoped roles (with
 * a Manage action), or an "all domains" note for admins/managers. */
function MemberDomainAccess({
  member,
  domainName,
  canManage,
  onManage,
}: {
  member: Member;
  domainName: Map<string, string>;
  canManage: boolean;
  onManage: () => void;
}) {
  if (!isEnvScoped(member.role)) {
    return <p className="mt-1 text-[0.7rem] text-muted">Access · all domains</p>;
  }
  const names = member.domainIds.map((id) => domainName.get(id)).filter(Boolean) as string[];
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {names.length > 0 ? (
        names.map((name) => (
          <span
            key={name}
            className="rounded-full bg-surface-2 px-2 py-0.5 text-[0.65rem] text-bone"
          >
            {name}
          </span>
        ))
      ) : (
        <span className="text-[0.7rem] text-muted">No domain access yet</span>
      )}
      {canManage && (
        <button
          type="button"
          onClick={onManage}
          className="text-[0.7rem] font-medium text-iris hover:underline"
        >
          Manage domains
        </button>
      )}
    </div>
  );
}
